import React, { useCallback, useEffect, useRef, useState } from "react";
import FilterIcon from "../../../Assets/ActionIcons/FilterIcon.svg";
import CloseIcon from "../../../Assets/ActionIcons/CloseIcon.svg";
import { InvoiceStatus } from "../../../Contracts/Invoice";
import Select from "../../../Components/Atoms/Select";
import { useCustomerStore } from "../../../Store/Customer";
import { useAuth0 } from "@auth0/auth0-react";
import { DropdownOption } from "../../../Components/Atoms/Dropdown";

type AllEnabledDisabled = "all" | "enabled" | "disabled";
type AllYesNo = "all" | "yes" | "no";

interface InvoiceFilters {
  invoiceStatus: InvoiceStatus | "all";
  autoCollect: AllEnabledDisabled;
  dateIssuedStart: Date | null;
  dateIssuedEnd: Date | null;
  dueDateStart: Date | null;
  dueDateEnd: Date | null;
  clientName: string;
  emailStatus: AllEnabledDisabled;
  invoiceSent: AllYesNo;
}

const defaultFilters: InvoiceFilters = {
  invoiceStatus: "all",
  autoCollect: "all",
  dateIssuedStart: null,
  dateIssuedEnd: null,
  dueDateStart: null,
  dueDateEnd: null,
  clientName: "",
  emailStatus: "all",
  invoiceSent: "all"
};

export interface InvoiceListPageFiltersProps {
  setFilter: (value: string) => void;
}

const InvoiceListPageFilters: React.FC<InvoiceListPageFiltersProps> = ({ setFilter }) => {
  const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { getCustomers, customers } = useCustomerStore();
  const { getAccessTokenSilently } = useAuth0();

  const [inputs, setInputs] = useState<InvoiceFilters>(defaultFilters);

  const [dirty, setDirty] = useState<Partial<InvoiceFilters>>({});

  const [search, setSearch] = useState<string | undefined>(undefined);

  const refreshData = useCallback(async () => {
    await getCustomers(getAccessTokenSilently, undefined, undefined, undefined, undefined, search);
  }, [getAccessTokenSilently, getCustomers, search]);

  useEffect(() => {
    refreshData();
  }, [refreshData, search]);

  const dropDownOptions: DropdownOption[] = customers.map(c => ({
    label: c.name,
    value: c.name
  }));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = <T extends keyof InvoiceFilters>(field: T, value: InvoiceFilters[T]) => {
    setDirty({ ...dirty, [field]: value });
  };

  const handleFilterRemove = (field: keyof InvoiceFilters) => {
    setInputs({ ...inputs, [field]: defaultFilters[field] });
    const filtersToApply = generateFilterConditions(true)
      .filter(entry => entry.fieldName !== field)
      .map(({ applyFilter }) => applyFilter);
    setFilter(filtersToApply.join(";"));
  };

  const generateFilterConditions = (
    includeDirty: boolean
  ): Array<{
    condition: boolean;
    fieldName: keyof InvoiceFilters;
    applyFilter: string;
    appliedLabel: string;
  }> => {
    let consolidatedDataForInputs = inputs;
    if (includeDirty) {
      consolidatedDataForInputs = {
        ...inputs,
        ...dirty
      };
    }

    return [
      {
        condition: consolidatedDataForInputs.invoiceStatus !== "all",
        fieldName: "invoiceStatus" as keyof InvoiceFilters,
        applyFilter: `status:eq:${consolidatedDataForInputs.invoiceStatus}`,
        appliedLabel: `Status: ${consolidatedDataForInputs.invoiceStatus}`
      },
      {
        condition: consolidatedDataForInputs.autoCollect !== "all",
        fieldName: "autoCollect" as keyof InvoiceFilters,
        applyFilter: `payments_invoice_auto_collection_status:${
          consolidatedDataForInputs.autoCollect === "enabled" ? "eq" : "ne"
        }:pending`,
        appliedLabel: `Auto Collect: ${
          consolidatedDataForInputs.autoCollect.charAt(0).toUpperCase() +
          consolidatedDataForInputs.autoCollect.slice(1)
        }`
      },
      {
        condition: !!consolidatedDataForInputs.dateIssuedStart,
        fieldName: "dateIssuedStart" as keyof InvoiceFilters,
        applyFilter: `issue_date:gte:${
          consolidatedDataForInputs.dateIssuedStart?.toISOString().split("T")[0]
        }`,
        appliedLabel: `Date Issued From: ${
          consolidatedDataForInputs.dateIssuedStart?.toISOString().split("T")[0]
        }`
      },
      {
        condition: !!consolidatedDataForInputs.dateIssuedEnd,
        fieldName: "dateIssuedEnd" as keyof InvoiceFilters,
        applyFilter: `issue_date:lte:${
          consolidatedDataForInputs.dateIssuedEnd?.toISOString().split("T")[0]
        }`,
        appliedLabel: `Date Issued To: ${
          consolidatedDataForInputs.dateIssuedEnd?.toISOString().split("T")[0]
        }`
      },
      {
        condition: !!consolidatedDataForInputs.dueDateStart,
        fieldName: "dueDateStart" as keyof InvoiceFilters,
        applyFilter: `due_date:gte:${
          consolidatedDataForInputs.dueDateStart?.toISOString().split("T")[0]
        }`,
        appliedLabel: `Due Date From: ${
          consolidatedDataForInputs.dueDateStart?.toISOString().split("T")[0]
        }`
      },
      {
        condition: !!consolidatedDataForInputs.dueDateEnd,
        fieldName: "dueDateEnd" as keyof InvoiceFilters,
        applyFilter: `due_date:lte:${
          consolidatedDataForInputs.dueDateEnd?.toISOString().split("T")[0]
        }`,
        appliedLabel: `Due Date To: ${
          consolidatedDataForInputs.dueDateEnd?.toISOString().split("T")[0]
        }`
      },
      {
        condition: consolidatedDataForInputs.clientName !== "",
        fieldName: "clientName" as keyof InvoiceFilters,
        applyFilter: `customer_name:eq:${consolidatedDataForInputs.clientName}`,
        appliedLabel: `Client: ${consolidatedDataForInputs.clientName}`
      },
      {
        condition: consolidatedDataForInputs.emailStatus !== "all",
        fieldName: "emailStatus" as keyof InvoiceFilters,
        applyFilter:
          consolidatedDataForInputs.emailStatus === "enabled"
            ? "invoice_emails_enabled:eq:true"
            : "invoice_emails_enabled:ne:true",
        appliedLabel: `Email Status: ${
          consolidatedDataForInputs.emailStatus.charAt(0).toUpperCase() +
          consolidatedDataForInputs.emailStatus.slice(1)
        }`
      },
      {
        condition: consolidatedDataForInputs.invoiceSent !== "all",
        fieldName: "invoiceSent" as keyof InvoiceFilters,
        applyFilter: `invoice_sent:eq:${consolidatedDataForInputs.invoiceSent}`,
        appliedLabel: `Invoice Sent: ${
          consolidatedDataForInputs.invoiceSent.charAt(0).toUpperCase() +
          consolidatedDataForInputs.invoiceSent.slice(1)
        }`
      }
    ].filter(entry => entry.condition);
  };

  const onApply = () => {
    setInputs(inputs => ({ ...inputs, ...dirty }));
    setDirty({});
    const filtersToApply = generateFilterConditions(true).map(({ applyFilter }) => applyFilter);
    setFilter(filtersToApply.join(";"));
    setFilterMenuOpen(false);
  };

  const appliedFilters = generateFilterConditions(false);

  const onReset = () => {
    setInputs(defaultFilters);
    setDirty({});
    setFilter("");
    setFilterMenuOpen(false);
  };

  const consolidatedDataForInputs = {
    ...inputs,
    ...dirty
  };

  return (
    <div className="relative flex py-[0.1rem]">
      <button
        className="p-2 border rounded-lg border-gray-300 hover:bg-gray-200 transition-all duration-500"
        onClick={() => setFilterMenuOpen(true)}
      >
        <img src={FilterIcon} alt="filter" />
      </button>
      {appliedFilters.length > 0 && (
        <div className="flex space-x-2 items-center">
          {appliedFilters.map(appliedFilter => (
            <div
              key={appliedFilter.fieldName}
              className="bg-gray-100 px-2 py-1 rounded-lg flex items-center space-x-1 mx-2"
            >
              <span>{appliedFilter.appliedLabel}</span>
              <button
                className="rounded-full hover:bg-gray-200 flex items-center justify-center p-1"
                onClick={() => handleFilterRemove(appliedFilter.fieldName)}
              >
                <img src={CloseIcon} alt="close" className="h-2 w-2" />
              </button>
            </div>
          ))}
        </div>
      )}
      {filterMenuOpen && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 w-72 bg-white rounded shadow-2xl z-20 p-3 ml-1 max-h-[calc(100vh-9rem)] overflow-y-auto"
        >
          <div className="flex flex-col my-3">
            <label>Invoice Status</label>
            <select
              className="p-2 border border-gray-200 rounded"
              value={consolidatedDataForInputs.invoiceStatus}
              onChange={e => handleChange("invoiceStatus", e.target.value as InvoiceStatus)}
            >
              <option key="all" value="all">
                All
              </option>
              {[
                InvoiceStatus.Open,
                InvoiceStatus.Paid,
                InvoiceStatus.Overdue,
                InvoiceStatus.Voided
              ].map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col mb-3">
            <label>Auto Collect</label>
            <select
              className="p-2 border border-gray-200 rounded"
              value={consolidatedDataForInputs.autoCollect}
              onChange={e => handleChange("autoCollect", e.target.value as AllEnabledDisabled)}
            >
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div className="flex flex-col mb-3">
            <label className="mb-2">Date Issued</label>
            <div className="grid grid-cols-3 gap-2">
              <label
                className="col-span-1
							"
              >
                Start
              </label>
              <input
                type="date"
                className="col-span-2 px-2 py-1 border border-gray-200 rounded"
                value={consolidatedDataForInputs.dateIssuedStart?.toISOString().split("T")[0]}
                onChange={e => handleChange("dateIssuedStart", new Date(e.target.value))}
              />
              <label className="col-span-1">End</label>
              <input
                type="date"
                className="col-span-2 px-2 py-1 border border-gray-200 rounded"
                value={consolidatedDataForInputs.dateIssuedEnd?.toISOString().split("T")[0]}
                onChange={e => handleChange("dateIssuedEnd", new Date(e.target.value))}
              />
            </div>
          </div>
          <div className="flex flex-col mb-3">
            <label className="mb-2">Due Date</label>
            <div className="grid grid-cols-3 gap-2">
              <label
                className="col-span-1
							"
              >
                Start
              </label>
              <input
                type="date"
                className="col-span-2 px-2 py-1 border border-gray-200 rounded"
                value={consolidatedDataForInputs.dueDateStart?.toISOString().split("T")[0]}
                onChange={e => handleChange("dueDateStart", new Date(e.target.value))}
              />
              <label className="col-span-1">End</label>
              <input
                type="date"
                className="col-span-2 px-2 py-1 border border-gray-200 rounded"
                value={inputs.dueDateEnd?.toISOString().split("T")[0]}
                onChange={e => handleChange("dueDateEnd", new Date(e.target.value))}
              />
            </div>
          </div>
          <div className="flex flex-col mb-3 space-y-2">
            <label>Client</label>
            <Select
              dropDownOptions={dropDownOptions}
              onChange={e => {
                const selectedCustomer = customers.find(c => c.name === e);
                handleChange("clientName", selectedCustomer?.name ?? "");
              }}
              value={consolidatedDataForInputs.clientName}
              onSearch={setSearch}
              searchWithDebounce={true}
              className="col-span-4"
              placeholder="Client Name"
              dropdownListClassName="max-h-60 overflow-y-auto"
            />
          </div>
          <div className="flex flex-col mb-3">
            <label>Email Status</label>
            <select
              className="p-2 border border-gray-200 rounded"
              value={consolidatedDataForInputs.emailStatus}
              onChange={e => handleChange("emailStatus", e.target.value as AllEnabledDisabled)}
            >
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div className="flex flex-col mb-3">
            <label>Invoice sent</label>
            <select
              className="p-2 border border-gray-200 rounded"
              value={consolidatedDataForInputs.invoiceSent}
              onChange={e => handleChange("invoiceSent", e.target.value as AllYesNo)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div className="sticky bottom-0 grid grid-cols-2 gap-4">
            <button
              className="rounded bg-blue-600 hover:bg-blue-500 text-white p-2"
              onClick={onApply}
            >
              Apply
            </button>
            <button
              className="rounded border border-gray-200 p-2 hover:bg-gray-100"
              onClick={onReset}
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceListPageFilters;
